import React, { useState } from "react";
import { CognitoUser } from "amazon-cognito-identity-js";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AuthLogo from "../../../images/auth-screen-logo.png";
import GifLogo from "../../../images/main-logo.gif";
import Pencil from "../../../images/pencil.png";
import UserPool from "../../../UserPool";
import Loader from '../../../components/Loader';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || "";

  const [formData, setFormData] = useState({
    email: emailFromState,
    verificationCode: ""
  });

  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.email || !formData.verificationCode) {
      setError("Please enter both email and verification code");
      return;
    }

    setLoading(true);

    const user = new CognitoUser({
      Username: formData.email,
      Pool: UserPool
    });

    user.confirmRegistration(formData.verificationCode, true, (err, result) => {
      setLoading(false);

      if (err) {
        console.error("Verification error:", err);

        if (err.code === "CodeMismatchException") {
          setError("Invalid verification code. Please check and try again");
        } else if (err.code === "ExpiredCodeException") {
          setError("Verification code has expired. Please request a new one");
        } else if (err.code === "NotAuthorizedException") {
          setError("User is already verified or verification code is invalid");
        } else {
          setError(err.message || "Failed to verify email");
        }
        return;
      }

      setSuccess("Email verified successfully! Redirecting to login...");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    });
  };

  const handleResendCode = async () => {
    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError("");

    const user = new CognitoUser({
      Username: formData.email,
      Pool: UserPool
    });

    user.resendConfirmationCode((err, result) => {
      setLoading(false);

      if (err) {
        console.error("Resend code error:", err);
        setError(err.message || "Failed to resend verification code");
        return;
      }

      setSuccess("Verification code sent to your email!");
      setTimeout(() => setSuccess(""), 3000);
    });
  };

  return (
    <div className="flex justify-between w-full h-auto min-h-screen bg-gradient-to-b from-primary-500 to-secondary-500 py-10 px-16">
      {isLoading && <Loader />}

      <div className="flex flex-col gap-5 h-full">
        <div className="flex items-center gap-3">
          <img src={AuthLogo} alt="logo" className="w-[60px]" />
          <h3 className="text-2xl text-white">Name.com</h3>
        </div>
        <div className="flex justify-center w-[485px]">
          <img src={GifLogo} alt="main-logo" className="w-[350px]" />
        </div>
        <div className="w-[485px]">
          <h1 className="text-center text-6xl text-text-light">
            Candidate <br />
            Onboarding and <br />
            Training
          </h1>
        </div>
      </div>

      <div className="flex flex-col justify-center items-center p-16 bg-white rounded-lg max-w-md">
        <h1 className="text-4xl mb-6">Verify Email</h1>
        <p className="text-text-hint text-center mb-8">
          We've sent a verification code to your email. Please enter it below to complete your registration.
        </p>

        <form onSubmit={handleVerify} className="w-full">
          <div className="flex flex-col gap-6">
            <div>
              <label htmlFor="email" className="text-text-hint block mb-2">
                Username / Email<span className="text-red-500">*</span>
              </label>
              <div className="flex items-center px-2 border border-neutral-500 rounded-lg">
                <input
                  id="email"
                  onChange={handleChange}
                  value={formData.email}
                  name="email"
                  type="text"
                  required
                  placeholder="Enter your username or email"
                  className="outline-none border-none focus:ring-0 flex-grow py-2"
                />
                <img src={Pencil} alt="pencil" className="w-6 h-6" />
              </div>
            </div>

            <div>
              <label htmlFor="verificationCode" className="text-text-hint block mb-2">
                Verification Code<span className="text-red-500">*</span>
              </label>
              <div className="flex items-center px-2 border border-neutral-500 rounded-lg">
                <input
                  id="verificationCode"
                  onChange={handleChange}
                  value={formData.verificationCode}
                  name="verificationCode"
                  type="text"
                  required
                  placeholder="Enter verification code"
                  className="outline-none border-none focus:ring-0 flex-grow py-2"
                />
                <img src={Pencil} alt="pencil" className="w-6 h-6" />
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-600 text-sm">{success}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 text-base rounded-full text-white bg-secondary-600 hover:bg-secondary-700 disabled:bg-neutral-300 disabled:cursor-not-allowed mt-6 mb-4"
          >
            {isLoading ? "Verifying..." : "Verify Email"}
          </button>

          <div className="text-center space-y-2">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading}
              className="text-info-500 text-sm hover:underline"
            >
              Resend Verification Code
            </button>
            <Link to="/login" className="text-info-500 text-sm block">
              Back to Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmail;
